const DEFAULT_CURRENCY = "USD";

const ISO_CODES = `
AED AFN ALL AMD AOA ARS AUD AWG AZN BAM BBD BDT BGN BHD BIF BMD BND BOB BRL BSD
BTN BWP BYN BZD CAD CDF CHF CLP CNY COP CRC CUP CVE CZK DJF DKK DOP DZD EGP ERN
ETB EUR FJD GBP GEL GHS GIP GMD GNF GTQ GYD HKD HNL HTG HUF IDR ILS INR IQD IRR
ISK JMD JOD JPY KES KGS KHR KMF KRW KWD KYD KZT LAK LBP LKR LRD LSL LYD MAD MDL
MGA MKD MMK MNT MOP MRU MUR MVR MWK MXN MYR MZN NAD NGN NIO NOK NPR NZD OMR PAB
PEN PGK PHP PKR PLN PYG QAR RON RSD RUB RWF SAR SBD SCR SDG SEK SGD SLE SOS SRD
SSP STN SVC SYP SZL THB TJS TMT TND TOP TRY TTD TWD TZS UAH UGX USD UYU UZS VES
VND VUV WST XAF XCD XOF XPF YER ZAR ZMW
`
  .trim()
  .split(/\s+/);

function metaFromCode(code) {
  let name = code;
  let symbol = code;
  let decimals = 2;
  try {
    name = new Intl.DisplayNames(["en"], { type: "currency" }).of(code) || code;
  } catch {
    /* keep code */
  }
  try {
    const nf = new Intl.NumberFormat("en", { style: "currency", currency: code });
    const parts = nf.formatToParts(0);
    symbol = parts.find((p) => p.type === "currency")?.value || code;
    const digits = nf.resolvedOptions().maximumFractionDigits;
    if (typeof digits === "number") decimals = digits;
  } catch {
    /* keep defaults */
  }
  return { code, name, symbol, decimals };
}

export const COMPANY_CURRENCIES = ISO_CODES.map(metaFromCode);

const BY_CODE = Object.fromEntries(COMPANY_CURRENCIES.map((c) => [c.code, c]));

export function isIsoCurrencyCode(raw) {
  return /^[A-Z]{3}$/.test(String(raw || "").trim().toUpperCase());
}

export function normalizeCurrency(raw) {
  const code = String(raw || "").trim().toUpperCase();
  return isIsoCurrencyCode(code) ? code : DEFAULT_CURRENCY;
}

export function currencyMeta(raw) {
  const code = normalizeCurrency(raw);
  return BY_CODE[code] || metaFromCode(code);
}

export function currencySymbol(raw) {
  return currencyMeta(raw).symbol;
}

function roundMoney(n, decimals = 2) {
  const f = 10 ** decimals;
  return Math.round((Number(n) || 0) * f) / f;
}

/** Each rate is units of that currency per 1 USD. converted = (amount / from) * to */
export function convertBetweenRates(amount, fromCurrency, toCurrency, rates) {
  const from = normalizeCurrency(fromCurrency);
  const to = normalizeCurrency(toCurrency);
  const n = Number(amount);
  const value = Number.isFinite(n) ? n : 0;
  if (from === to) return roundMoney(value, currencyMeta(to).decimals);
  const table = rates && typeof rates === "object" ? rates : { USD: 1 };
  const rf = Number(table[from]);
  const rt = Number(table[to]);
  if (!Number.isFinite(rf) || !Number.isFinite(rt) || rf <= 0 || rt <= 0) {
    return roundMoney(value, currencyMeta(to).decimals);
  }
  return roundMoney((value / rf) * rt, currencyMeta(to).decimals);
}

export function fromUsd(amount, toCurrency, rates) {
  return convertBetweenRates(amount, "USD", toCurrency, rates);
}

export function toUsd(amount, fromCurrency, rates) {
  return convertBetweenRates(amount, fromCurrency, "USD", rates);
}

/** Format an amount already in `currency`. */
export function formatCompanyMoney(amount, currency) {
  const n = Number(amount);
  const meta = currencyMeta(currency);
  const value = Number.isFinite(n) ? n : 0;
  const abs = Math.abs(value);
  const formatted =
    meta.decimals === 0 ? String(Math.round(abs)) : abs.toFixed(meta.decimals);
  const signed = value < 0 ? "-" : "";
  if (meta.code === "USD") return `${signed}${meta.symbol}${formatted}`;
  return `${signed}${meta.symbol} ${formatted}`;
}

/** Convert `amount` from `fromCurrency` into `toCurrency`, then format. */
export function formatConvertedMoney(amount, fromCurrency, toCurrency, rates) {
  return formatCompanyMoney(
    convertBetweenRates(amount, fromCurrency, toCurrency, rates),
    toCurrency,
  );
}

export function companyCurrencyOf(company) {
  return normalizeCurrency(company?.currency);
}
